import { RefObject, SyntheticEvent, useEffect, useState } from "react";
import { Box, Fab, IconButton, InputBase, Paper, Stack, Typography } from "@mui/material";
import { ArrowUpwardRounded, Close, Edit, MicNoneRounded, Stop } from "@mui/icons-material";
import { useCase } from "../../provider/CaseProvider";
import { useAiChat } from "../../provider/AiChatProvider";
import { useDrawer } from "../../provider/DrawerProvider";
import { useAiEventStreamControls, useAiEventStreamSnapshot } from "../../hooks/useAiEventStream";
import { useAiEventStreamRegistry } from "../../provider/AiEventStreamProvider";
import { createResource } from "../../services/ReactQuery/reactQueryService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelAiChatWorkflow, refreshAiChatThread, sendAiChatPrompt } from "../../services/aiChatService";
import { canCancelVoiceRecording } from "../../services/aiChatVoiceInputService";
import { VoiceRecordingStatus } from "../../types/AIChat/AiChatVoiceInput";
import VoiceRecordingBar from "./VoiceRecordingBar";
import { useAiChatVoiceInput } from "./useAiChatVoiceInput";
import aiChatInputStyles from "./aiChatInputStyles";
import ReactGA from "react-ga4";
import { enqueueSnackbar } from "notistack";
import ToolTipIconButton from "../Button/ToolTipIconButton.tsx";
import { translate } from "../../services/Translations/translatorService.tsx";
import AiChatSkills from "./AiChatSkills.tsx";

const VOICE_BAR_VISIBLE_STATUSES: VoiceRecordingStatus[] = ["recording", "transcribing", "sending"];

type AIChatInputProps = {
  ref: RefObject<HTMLTextAreaElement | null>;
};

export default function AIChatInput({ ref }: AIChatInputProps) {
  const [prompt, setPrompt] = useState("");

  const { product } = useCase();
  const { flushDraftSave, closeDrawer, drawerState } = useDrawer();

  const {
    activeThreadId,
    setActiveThreadId,
    setIsCreatingThread,
    isLoading,
    activeAgent,
    activeEditStepId,
    isCancellationRequested,
    setIsCancellationRequested,
  } = useAiChat();
  const { getStore: getStreamStore } = useAiEventStreamRegistry();
  const { reset, getCurrentStreamId } = useAiEventStreamControls(activeThreadId);
  const { status: streamStatus } = useAiEventStreamSnapshot(activeThreadId);
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const voice = useAiChatVoiceInput({ prompt, setPrompt, inputRef: ref });

  if (!product) {
    return null;
  }

  useEffect(() => {
    voice.resetVoiceInput();
    setPrompt("");
  }, [activeThreadId]);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const sendPrompt = async (event: SyntheticEvent) => {
    event.preventDefault();
    setPrompt("");
    try {
      if (activeEditStepId !== null) {
        await flushDraftSave();
      }
      await sendAiChatPrompt({
        product,
        activeThreadId,
        setActiveThreadId,
        createMutation,
        queryClient,
        getStreamStore,
        prompt: prompt,
        setIsCreatingThread,
        agentType: activeAgent,
        activeExecutionStepId: activeEditStepId,
      });
    } catch {
      enqueueSnackbar("Die Nachricht konnte nicht gesendet werden.", { variant: "error" });
    }
  };

  const cancelWorkflow = async (threadId: number) => {
    const streamId = getCurrentStreamId();
    setIsCancellationRequested(threadId, true);
    try {
      await cancelAiChatWorkflow(threadId);
      await refreshAiChatThread(queryClient, threadId);
      if (getCurrentStreamId() === streamId) {
        reset();
      }
    } catch {
      enqueueSnackbar("Die Antwort konnte nicht abgebrochen werden.", { variant: "error" });
    } finally {
      setIsCancellationRequested(threadId, false);
    }
  };

  const isVoiceBarVisible = VOICE_BAR_VISIBLE_STATUSES.includes(voice.status);
  const isInputDisabled = isLoading || voice.status === "requestingPermission";
  const stopButtonThreadId = isLoading && streamStatus === "running" ? activeThreadId : null;
  const showStopButton = stopButtonThreadId !== null;

  const voiceBar =
    voice.status !== "idle" || voice.waveform !== null ? (
      <VoiceRecordingBar
        visible={isVoiceBarVisible}
        isTranscribing={voice.status === "transcribing" || voice.status === "sending"}
        canCancel={canCancelVoiceRecording(voice.status)}
        statusMessage={voice.status === "sending" ? "Nachricht wird gesendet …" : "Wird transkribiert …"}
        waveform={voice.waveform}
        onCancel={voice.cancelVoiceRecording}
        onStop={voice.stopAndInsertVoiceRecording}
        onSend={voice.finishVoiceRecording}
      />
    ) : null;

  return (
    <Box
      sx={{
        margin: { xs: "0.25rem", sm: "0.75rem 1rem" },
      }}
    >
      {activeEditStepId && (
        <Paper variant={"outlined"} sx={{ margin: "0 auto", width: "90%", paddingX: "1rem" }}>
          <Stack direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
            <Edit sx={{ color: "accent.main" }} />
            <Typography>
              <strong>{translate("drawer.type." + drawerState?.type, "N/A")}</strong>
            </Typography>
            <Typography>•</Typography>
            <Typography color={"accent"}>
              <strong>Bearbeitung aktiv</strong>
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <ToolTipIconButton icon={Close} tooltip={"Schrittbearbeitung beenden"} onClick={closeDrawer} />
          </Stack>
        </Paper>
      )}
      {voiceBar}
      {!isVoiceBarVisible && (
        <form onSubmit={sendPrompt}>
          <Stack sx={aiChatInputStyles.composer} spacing={4}>
            <InputBase
              value={prompt}
              onChange={(event) => {
                setPrompt(event.target.value);
              }}
              fullWidth
              multiline
              disabled={isInputDisabled}
              inputRef={ref}
              maxRows={15}
              placeholder={
                activeEditStepId
                  ? "Änderung für diesen Schritt eingeben..."
                  : `Frage ${activeAgent} zur Akte oder gib Anweisungen...`
              }
              sx={{ padding: "0.5rem 0.75rem 0" }}
              onKeyDown={async (event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  await sendPrompt(event);
                }
              }}
            />
            <Stack
              direction={"row"}
              spacing={0.5}
              sx={{ alignItems: "center", justifyContent: "flex-end", cursor: "text" }}
            >
              <AiChatSkills />
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                onClick={() => {
                  ReactGA.event({
                    category: "UserAction",
                    action: "startVoiceRecording",
                  });
                  voice.startVoiceRecording();
                }}
                disabled={isInputDisabled}
              >
                <MicNoneRounded />
              </IconButton>
              <Fab
                type={showStopButton ? "button" : "submit"}
                size={"small"}
                color={"primary"}
                sx={{
                  boxShadow: "none",
                  "&:hover, &:active, &.Mui-focusVisible": { boxShadow: "none" },
                }}
                disabled={showStopButton ? isCancellationRequested : isInputDisabled}
                {...(stopButtonThreadId !== null ? { onClick: () => cancelWorkflow(stopButtonThreadId) } : {})}
              >
                {showStopButton ? <Stop /> : <ArrowUpwardRounded />}
              </Fab>
            </Stack>
          </Stack>
        </form>
      )}
    </Box>
  );
}
